import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import { IMG, OFFICIAL } from "@/data/media";
import { HomeHero } from "@/components/HomeHero";
import { Marquee } from "@/components/Marquee";
import { FadeUp, SectionHead } from "@/components/motion";
import { DonateCards } from "@/components/DonateCards";
import { ProjectCards } from "@/components/ProjectCards";
import { ActivitiesBento } from "@/components/ActivitiesBento";
import { Lightbox } from "@/components/Lightbox";
import { L } from "@/components/L";

const AboutTeaser = () => {
  const { t } = useLang();
  return (
    <section id="home-about" className="py-24 sm:py-32" data-testid="home-about">
      <div className="wrap grid gap-14 lg:grid-cols-12 items-center">
        <FadeUp className="lg:col-span-5 relative order-2 lg:order-1">
          <div className="relative aspect-[4/5] overflow-hidden petal-tr-lg img-zoom bg-[var(--blue-pale)]">
            <img src={IMG.girlBlue} alt={t.gallery.captions.girlBlue} loading="lazy" className="photo absolute inset-0 w-full h-full object-cover" />
          </div>
          <div aria-hidden="true" className="absolute -right-5 -bottom-5 w-24 h-24 petal-bl bg-[var(--yellow)]" />
        </FadeUp>
        <div className="lg:col-span-7 order-1 lg:order-2 lg:pl-8">
          <span className="eyebrow">{t.home.aboutEyebrow}</span>
          <FadeUp as="h2" className="mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight" data-testid="home-about-title">
            {t.home.aboutTitle}
          </FadeUp>
          <FadeUp delay={0.1} as="p" className="mt-8 text-base sm:text-lg leading-relaxed text-[var(--ink-soft)] max-w-xl">{t.home.aboutText}</FadeUp>
          <FadeUp delay={0.15} className="mt-8 grid sm:grid-cols-2 gap-4 max-w-xl">
            {t.about.values.slice(0, 2).map(([title, text]) => (
              <div key={title} className="border-t-2 border-[var(--green-deep)] pt-4">
                <p className="font-display text-2xl">{title}</p>
                <p className="mt-2 text-sm text-[var(--ink-soft)] leading-relaxed">{text}</p>
              </div>
            ))}
          </FadeUp>
          <FadeUp delay={0.2}><L to="about" className="btn btn-ghost mt-10" data-testid="home-about-link">{t.common.discover} <ArrowUpRight size={16} /></L></FadeUp>
        </div>
      </div>
    </section>
  );
};

const QuoteBand = () => {
  const { t } = useLang();
  return (
    <section className="relative bg-[var(--green-night)] text-white py-24 sm:py-32 overflow-hidden grain" data-testid="home-quote">
      <div className="wrap grid gap-12 lg:grid-cols-12 items-center">
        <div className="lg:col-span-4">
          <FadeUp className="aspect-square overflow-hidden petal-bl max-w-sm">
            <img src={IMG.smilingGirls} alt={t.gallery.captions.smilingGirls} loading="lazy" className="photo w-full h-full object-cover" />
          </FadeUp>
        </div>
        <FadeUp className="lg:col-span-8" delay={0.1}>
          <blockquote className="font-display italic text-4xl sm:text-5xl lg:text-6xl leading-[1.1]">{t.home.quote}</blockquote>
          <p className="mt-6 text-white/75 text-sm uppercase tracking-[0.2em]">Fundación MADE</p>
        </FadeUp>
      </div>
    </section>
  );
};

const TeamTeaser = () => {
  const { t } = useLang();
  const tm = t.team;
  return (
    <section className="py-24 sm:py-32" data-testid="home-team">
      <div className="wrap">
        <SectionHead eyebrow={t.home.teamEyebrow} title={t.home.teamTitle} className="max-w-3xl" />
        <div className="mt-14 grid gap-6 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7 grid sm:grid-cols-[minmax(0,240px)_1fr] gap-8 items-center bg-white p-6 sm:p-8 rounded-[2.5rem_2.5rem_2.5rem_0.5rem] shadow-[0_0_0_1px_var(--line)]">
            <div className="aspect-square overflow-hidden petal-bl bg-[var(--blue-pale)] max-w-[240px]">
              <img src={IMG.drDennis} alt={tm.directorName} loading="lazy" className="photo w-full h-full object-cover" />
            </div>
            <div>
              <p className="eyebrow">{tm.directorRole}</p>
              <h3 className="mt-3 text-3xl leading-tight" data-testid="home-director-name">{tm.directorName}</h3>
              <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">{tm.directorBio[0]}</p>
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-5 bg-[var(--coral-pale)] p-8 rounded-[2.5rem_0.5rem_2.5rem_2.5rem] flex flex-col">
            <p className="eyebrow">{tm.presidentRole}</p>
            <h3 className="mt-3 text-3xl leading-tight" data-testid="home-president-name">{tm.presidentName}</h3>
            <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">{tm.presidentBio}</p>
            <L to="team" className="btn btn-ghost mt-auto self-start !mt-8" data-testid="home-team-link">{t.common.discover} <ArrowUpRight size={16} /></L>
          </FadeUp>
        </div>
        <FadeUp className="mt-6 flex flex-wrap gap-2" delay={0.15}>
          {t.projects.laRomana.specialists.map((s) => <span key={s} className="px-4 py-2 rounded-full bg-[var(--green-pale)] text-[var(--green-deep)] text-sm font-semibold">{s}</span>)}
        </FadeUp>
      </div>
    </section>
  );
};

const STRIP = ["kidsBanner", "childCap", "teamShirts", "baby", "volunteerChild", "twoChildren"];
const CORNERS = ["petal-bl", "frame-soft", "petal-tr", "frame-soft", "petal-br", "frame-soft"];

const GalleryTeaser = () => {
  const { t } = useLang();
  const [idx, setIdx] = useState(null);
  const items = STRIP.map((k) => ({ src: IMG[k], caption: t.gallery.captions[k] }));
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="home-gallery">
      <div className="wrap flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <SectionHead eyebrow={t.home.galleryEyebrow} title={t.home.galleryTitle} />
        <FadeUp><L to="gallery" className="btn btn-ghost" data-testid="home-gallery-link">{t.common.viewAll} <ArrowUpRight size={16} /></L></FadeUp>
      </div>
      <div className="wrap mt-14 grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
        {items.map((it, i) => (
          <FadeUp key={it.src} delay={i * 0.06} className={i % 2 ? "lg:mt-16" : ""}>
            <button onClick={() => setIdx(i)} aria-label={`${t.gallery.open}: ${it.caption}`} className={`block w-full aspect-[3/4] overflow-hidden img-zoom ${CORNERS[i]} bg-[var(--green-pale)]`} data-testid={`home-gallery-item-${i}`}>
              <img src={it.src} alt={it.caption} loading="lazy" className="photo w-full h-full object-cover" />
            </button>
          </FadeUp>
        ))}
      </div>
      <Lightbox items={items} index={idx} onClose={() => setIdx(null)} onChange={setIdx} />
    </section>
  );
};

const ContactBand = () => {
  const { t } = useLang();
  return (
    <section className="py-24 sm:py-32" data-testid="home-contact">
      <div className="wrap">
        <FadeUp className="relative overflow-hidden bg-[var(--green-deep)] text-white rounded-[3rem_3rem_3rem_0.6rem] p-8 sm:p-14 lg:p-20">
          <div aria-hidden="true" className="absolute -right-10 -top-10 w-56 h-56 petal-bl bg-[var(--yellow)] opacity-90" />
          <div aria-hidden="true" className="absolute right-40 -bottom-16 w-36 h-36 petal-tr bg-[var(--coral)] opacity-90 hidden md:block" />
          <span className="eyebrow !text-[var(--yellow)]">{t.home.contactEyebrow}</span>
          <h2 className="relative mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">{t.home.contactTitle}</h2>
          <p className="relative mt-6 text-white/85 text-lg max-w-xl">{t.home.contactText}</p>
          <div className="relative mt-10 flex flex-wrap gap-3">
            <L to="contact" className="btn btn-yellow" data-testid="home-contact-btn">{t.nav.contact} <ArrowUpRight size={16} /></L>
            <a href={`mailto:${OFFICIAL.email}`} className="btn btn-ghost-light break-all" data-testid="home-contact-email">{OFFICIAL.email}</a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLang();
  return (
    <>
      <HomeHero />
      <Marquee words={t.home.marquee} />
      <AboutTeaser />
      <section className="py-24 sm:py-32 bg-white" data-testid="home-activities">
        <div className="wrap">
          <SectionHead eyebrow={t.home.activitiesEyebrow} title={t.home.activitiesTitle} className="max-w-3xl" />
          <div className="mt-14"><ActivitiesBento /></div>
        </div>
      </section>
      <section className="py-24 sm:py-32" data-testid="home-projects">
        <div className="wrap">
          <SectionHead eyebrow={t.home.projectsEyebrow} title={t.home.projectsTitle} className="max-w-3xl" />
          <div className="mt-14"><ProjectCards /></div>
        </div>
      </section>
      <QuoteBand />
      <TeamTeaser />
      <GalleryTeaser />
      <section className="py-24 sm:py-32" data-testid="home-donate">
        <div className="wrap">
          <SectionHead eyebrow={t.home.donateEyebrow} title={t.home.donateTitle} className="max-w-3xl" />
          <FadeUp as="p" className="mt-6 text-[var(--ink-soft)] text-lg max-w-2xl">{t.donate.intro}</FadeUp>
          <div className="mt-12"><DonateCards /></div>
        </div>
      </section>
      <ContactBand />
    </>
  );
}
